import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Link, resolveRoute} from '@computerrock/formation-router';
import {useTranslate} from '@computerrock/formation-i18n';
import {useStyles, Circle, Triangle, Card, ScrollToTop} from '@ace-de/ui-components';
import {Icon, phoneIcon, emailIcon, overviewIcon, notesThinIcon, editThinIcon} from '@ace-de/ui-components/icons';
import ContactDetailRow from './view-elements/ContactDetailRow';
import ContactCard from './view-elements/ContactCard';
import routePaths from '../routePaths';
import invoiceSubmissionTabs from '../invoice-submission/constants/invoiceSubmissionTabs';
import {contactInfo} from './constants/contact';
import * as invoiceSubmissionActionTypes from '../invoice-submission/invoiceSubmissionActionTypes';
import styles from './DashboardScreen.module.scss';

const DashboardScreen = props => {
    const {cx} = useStyles(props, styles);
    const {createTranslateShorthand} = useTranslate();
    const translateScreen = createTranslateShorthand('dashboard_screen');
    const {invoiceSubmissionDraft, initiateInvoiceSubmissionDraftDeletion} = props;

    return (
        <div
            className={cx([
                'global!ace-u-flex',
                'global!ace-u-flex--small-direction-column',
                'ace-c-dashboard-screen',
            ])}
        >
            <ScrollToTop className={styles['ace-c-dashboard-screen']} />
            <h1
                className={cx([
                    'global!ace-u-typography--variant-h1',
                    'global!ace-u-margin--small-bottom-24',
                ])}
            >
                {translateScreen('title.welcome')}
            </h1>
            <p
                className={cx([
                    'global!ace-u-typography--variant-body-bold',
                    'global!ace-u-margin--small-bottom-24',
                ])}
            >
                {translateScreen('text.about')}
            </p>
            <div
                className={cx([
                    'global!ace-u-flex',
                    'global!ace-u-flex--small-direction-column',
                    'global!ace-u-flex--x-large-direction-row',
                    'global!ace-u-margin--x-large-bottom-64',
                    'global!ace-u-padding--x-large-bottom-64',
                ])}
            >
                {!!invoiceSubmissionDraft && (
                    <Card
                        className={cx([
                            'ace-c-card--variant-white',
                            'global!ace-u-margin--small-bottom-24',
                            'global!ace-u-margin--x-large-right-24',
                            'global!ace-u-flex--large-basis-30',
                        ])}
                    >
                        <Card
                            className={cx([
                                'global!ace-u-padding--large-64',
                                'global!ace-u-padding--small-48',
                                'global!ace-u-flex',
                                'global!ace-u-flex--align-center',
                                'global!ace-u-flex--justify-center',
                                'ace-c-card--variant-blue',
                            ])}
                        >
                            <div className={cx('global!ace-u-padding--large-64')}>
                                <Circle
                                    className={cx([
                                        'global!ace-u-flex',
                                        'global!ace-u-flex--align-center',
                                        'global!ace-u-flex--justify-center',
                                        'ace-c-circle--medium-responsive',
                                    ])}
                                >
                                    <Icon
                                        icon={editThinIcon}
                                        className={cx([
                                            'ace-c-icon--xl',
                                            'ace-c-icon--color-highlight',
                                        ])}
                                    />
                                </Circle>
                            </div>
                        </Card>
                        <div
                            className={cx([
                                'global!ace-u-padding--small-top-16',
                                'global!ace-u-padding--small-right-16',
                                'global!ace-u-padding--small-left-16',
                                'global!ace-u-padding--small-bottom-32',
                                'global!ace-u-padding--x-large-top-32',
                                'global!ace-u-padding--x-large-right-32',
                                'global!ace-u-padding--x-large-left-32',
                                'global!ace-u-padding--x-large-bottom-64',
                                'global!ace-u-position--relative',
                            ])}
                        >
                            <Triangle className={cx('global!ace-u-display--large-none')} />
                            <p
                                className={cx([
                                    'global!ace-u-typography--variant-body-bold',
                                    'global!ace-u-padding--small-bottom-8',
                                ])}
                            >
                                {translateScreen('invoice_submission_draft_panel.text')}
                            </p>
                            <Link
                                to={invoiceSubmissionDraft.serviceCaseId
                                && !invoiceSubmissionDraft.serviceCaseId.includes('MIA')
                                    ? resolveRoute(routePaths.INVOICE_SUBMISSION_CREATION, {
                                        serviceCaseId: invoiceSubmissionDraft.serviceCaseId,
                                        invoiceSubmissionId: invoiceSubmissionDraft.invoiceSubmissionId,
                                    })
                                    : resolveRoute(routePaths.NEW_INVOICE_SUBMISSION_CREATION, {
                                        invoiceSubmissionId: invoiceSubmissionDraft.invoiceSubmissionId,
                                    })}
                            >
                                <p className={cx('global!ace-u-typography--variant-text-link')}>
                                    {translateScreen('invoice_submission_draft_panel.button')}
                                </p>
                            </Link>
                        </div>
                    </Card>
                )}
                <Card
                    className={cx([
                        'ace-c-card--variant-white',
                        'global!ace-u-margin--small-bottom-24',
                        'global!ace-u-margin--x-large-right-24',
                    ], {
                        'global!ace-u-flex--large-basis-30': !!invoiceSubmissionDraft,
                        'global!ace-u-flex--large-basis-50': !invoiceSubmissionDraft,
                    })}
                >
                    <Card
                        className={cx([
                            'global!ace-u-padding--large-64',
                            'global!ace-u-padding--small-48',
                            'global!ace-u-flex',
                            'global!ace-u-flex--align-center',
                            'global!ace-u-flex--justify-center',
                            'ace-c-card--variant-blue',
                        ])}
                    >
                        <div className={cx('global!ace-u-padding--large-64')}>
                            <Circle
                                className={cx([
                                    'global!ace-u-flex',
                                    'global!ace-u-flex--align-center',
                                    'global!ace-u-flex--justify-center',
                                    'ace-c-circle--medium-responsive',
                                ])}
                            >
                                <Icon
                                    icon={notesThinIcon}
                                    className={cx([
                                        'ace-c-icon--xxl',
                                        'ace-c-icon--color-highlight',
                                    ])}
                                />
                            </Circle>
                        </div>
                    </Card>
                    <div
                        className={cx([
                            'global!ace-u-padding--small-top-16',
                            'global!ace-u-padding--small-right-16',
                            'global!ace-u-padding--small-left-16',
                            'global!ace-u-padding--small-bottom-32',
                            'global!ace-u-padding--x-large-top-32',
                            'global!ace-u-padding--x-large-right-32',
                            'global!ace-u-padding--x-large-left-32',
                            'global!ace-u-padding--x-large-bottom-64',
                            'global!ace-u-position--relative',
                        ])}
                    >
                        <Triangle className={cx('global!ace-u-display--large-none')} />
                        <p
                            className={cx([
                                'global!ace-u-typography--variant-body-bold',
                                'global!ace-u-padding--small-bottom-8',
                            ])}
                        >
                            {translateScreen('service_case_panel.text')}
                        </p>
                        {invoiceSubmissionDraft ? (
                            <p
                                onClick={initiateInvoiceSubmissionDraftDeletion}
                                className={cx('global!ace-u-typography--variant-text-link')}
                            >
                                {translateScreen('service_case_panel.button')}
                            </p>
                        ) : (
                            <Link to={resolveRoute(routePaths.SERVICE_CASES)}>
                                <p className={cx('global!ace-u-typography--variant-text-link')}>
                                    {translateScreen('service_case_panel.button')}
                                </p>
                            </Link>
                        )}
                    </div>
                </Card>
                <Card
                    className={cx([
                        'ace-c-card--variant-white',
                        'global!ace-u-margin--small-bottom-24',
                    ], {
                        'global!ace-u-flex--large-basis-30': !!invoiceSubmissionDraft,
                        'global!ace-u-flex--large-basis-50': !invoiceSubmissionDraft,
                    })}
                >
                    <Card
                        className={cx([
                            'global!ace-u-padding--large-64',
                            'global!ace-u-padding--small-48',
                            'global!ace-u-flex',
                            'global!ace-u-flex--align-center',
                            'global!ace-u-flex--justify-center',
                            'ace-c-card--variant-blue',
                        ])}
                    >
                        <div className={cx('global!ace-u-padding--large-64')}>
                            <Circle
                                className={cx([
                                    'global!ace-u-flex',
                                    'global!ace-u-flex--align-center',
                                    'global!ace-u-flex--justify-center',
                                    'ace-c-circle--medium-responsive',
                                ])}
                            >
                                <Icon
                                    icon={overviewIcon}
                                    className={cx([
                                        'ace-c-icon--xxl',
                                        'ace-c-icon--color-highlight',
                                    ])}
                                />
                            </Circle>
                        </div>
                    </Card>
                    <div
                        className={cx([
                            'global!ace-u-padding--small-top-16',
                            'global!ace-u-padding--small-right-16',
                            'global!ace-u-padding--small-left-16',
                            'global!ace-u-padding--small-bottom-32',
                            'global!ace-u-padding--x-large-top-32',
                            'global!ace-u-padding--x-large-right-32',
                            'global!ace-u-padding--x-large-left-32',
                            'global!ace-u-padding--x-large-bottom-64',
                            'global!ace-u-position--relative',
                        ])}
                    >
                        <Triangle className={cx('global!ace-u-display--large-none')} />
                        <p
                            className={cx([
                                'global!ace-u-typography--variant-body-bold',
                                'global!ace-u-padding--small-bottom-8',
                            ])}
                        >
                            {translateScreen('invoice_submissions_panel.text')}
                        </p>
                        <Link
                            to={resolveRoute(
                                routePaths.INVOICE_SUBMISSIONS,
                                {activeTab: invoiceSubmissionTabs.ACTIVE},
                            )}
                        >
                            <p className={cx('global!ace-u-typography--variant-text-link')}>
                                {translateScreen('invoice_submissions_panel.button')}
                            </p>
                        </Link>
                    </div>
                </Card>
            </div>
            <ContactCard>
                <h3
                    className={cx([
                        'global!ace-u-typography--variant-body-medium',
                        'global!ace-u-margin--small-bottom-24',
                        'global!ace-u-margin--x-large-bottom-0',
                        'global!ace-u-margin--x-large-right-48',
                    ])}
                >
                    {translateScreen('contact_panel.title.contact_member_service')}
                </h3>
                <ContactDetailRow>
                    <Icon
                        className={cx([
                            'ace-c-icon--m',
                            'ace-c-icon--color-highlight',
                            'global!ace-u-margin--right-8',
                        ])}
                        icon={emailIcon}
                    />
                    <a href={contactInfo.mail.link} className={cx(['global!ace-u-typography--variant-body-medium'])}>
                        {contactInfo.mail.text}
                    </a>
                </ContactDetailRow>
                <div className={cx(['global!ace-u-margin--bottom-16', 'global!ace-u-margin--x-large-right-48'])} />
                <ContactDetailRow>
                    <Icon
                        className={cx([
                            'ace-c-icon--m',
                            'ace-c-icon--color-highlight',
                            'global!ace-u-margin--right-8',
                        ])}
                        icon={phoneIcon}
                    />
                    <a href={contactInfo.phone.link} className={cx(['global!ace-u-typography--variant-body-medium'])}>
                        {contactInfo.phone.text}
                    </a>
                </ContactDetailRow>
            </ContactCard>
        </div>
    );
};

DashboardScreen.propTypes = {
    invoiceSubmissionDraft: PropTypes.object,
    initiateInvoiceSubmissionDraftDeletion: PropTypes.func.isRequired,
};

DashboardScreen.defaultProps = {
    invoiceSubmissionDraft: null,
};

const mapStateToProps = state => ({
    invoiceSubmissionDraft: state.invoiceSubmissions.invoiceSubmissionDraft,
});

const mapDispatchToProps = dispatch => ({
    initiateInvoiceSubmissionDraftDeletion: payload => dispatch({
        type: invoiceSubmissionActionTypes.INITIATE_DELETE_INVOICE_SUBMISSION_DRAFT_FLOW,
        payload,
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(DashboardScreen);
